/**
 * RBAC from Firebase Auth custom claims.
 *
 * Set via Admin SDK (comma-separated atoms), e.g.:
 *   photos: "readall,updatetags"
 *   recipes: "readwriteall,delete"
 *
 * Atoms (per product spec):
 *   recipes: readall | readwriteall | none | delete
 *   photos: readall | readwriteall | none | updatetags | updatedescription | delete
 *
 * Hub apps: `forum`, `contacts`, `calendar` — optional comma scopes: `read`, `readwrite`, `readwriteall`, or `true`.
 *
 * Legacy: `approved: true` keeps full family access (photos + recipes + hub) unless overridden by explicit `none`.
 */

export function parseScopeClaim(raw) {
  if (raw == null || raw === undefined) return [];
  const s = String(raw).trim().toLowerCase();
  if (!s) return [];
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

export function hasPhotoScope(claims) {
  if (claims?.approved === true) return true;
  const p = parseScopeClaim(claims?.photos);
  if (p.includes('none')) return false;
  return p.some((x) =>
    ['readall', 'readwriteall', 'updatetags', 'updatedescription', 'delete'].includes(x)
  );
}

export function hasRecipeScope(claims) {
  if (claims?.approved === true) return true;
  const r = parseScopeClaim(claims?.recipes);
  if (r.includes('none')) return false;
  return r.some((x) => ['readall', 'readwriteall', 'delete'].includes(x));
}

/** Optional hub apps: comma scopes `read`, `readwrite`, `readwriteall`, or claim `true`. */
function hasHubAppScope(claims, claimKey) {
  if (claims?.approved === true) return true;
  if (claims?.[claimKey] === true) return true;
  const atoms = parseScopeClaim(claims?.[claimKey]);
  if (atoms.includes('none')) return false;
  return atoms.some((x) => ['read', 'readwrite', 'readall', 'readwriteall'].includes(x));
}

export function hasForumScope(claims) {
  return hasHubAppScope(claims, 'forum');
}

export function hasContactsScope(claims) {
  return hasHubAppScope(claims, 'contacts');
}

export function hasCalendarScope(claims) {
  return hasHubAppScope(claims, 'calendar');
}

/** Signed-in user may see the home landing (any app area). */
export function hasSiteAccess(claims) {
  if (claims?.approved === true) return true;
  return (
    hasPhotoScope(claims) ||
    hasRecipeScope(claims) ||
    hasForumScope(claims) ||
    hasContactsScope(claims) ||
    hasCalendarScope(claims)
  );
}

export function canReadRecipes(claims) {
  return hasRecipeScope(claims);
}

export function canWriteRecipes(claims) {
  if (claims?.approved === true) return true;
  const r = parseScopeClaim(claims?.recipes);
  if (r.includes('none')) return false;
  return r.some((x) => ['readwriteall', 'delete'].includes(x));
}

export function canDeleteRecipes(claims) {
  if (claims?.approved === true) return true;
  return parseScopeClaim(claims?.recipes).includes('delete');
}

export function canReadPhotos(claims) {
  return hasPhotoScope(claims);
}

export function canWritePhotoMetadata(claims) {
  if (claims?.approved === true) return true;
  const p = parseScopeClaim(claims?.photos);
  if (p.includes('none')) return false;
  return p.some((x) =>
    ['readwriteall', 'updatetags', 'updatedescription', 'delete'].includes(x)
  );
}

export function canUploadPhotos(claims) {
  if (claims?.approved === true) return true;
  const p = parseScopeClaim(claims?.photos);
  return p.includes('readwriteall') || p.includes('delete');
}

export function canDeletePhotos(claims) {
  if (claims?.approved === true) return true;
  return parseScopeClaim(claims?.photos).includes('delete');
}

/** Global tag vocabulary file (Cloud Function) — not for tag-only facet editors. */
export function canEditGlobalTagList(claims) {
  if (claims?.approved === true) return true;
  const p = parseScopeClaim(claims?.photos);
  return p.includes('readwriteall') || p.includes('delete');
}
